import * as React from 'react'

import { Skeleton } from '@ui/components/ui/skeleton'
import { cn } from '@ui/lib/utils'

export interface LoadingSkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Welche Form der Platzhalter hat. */
  variant?: 'text' | 'table' | 'card' | 'form'
  /** Anzahl der Platzhalterzeilen bzw. -karten. */
  rows?: number
  /** Nur für variant="table": Anzahl der Spalten. */
  columns?: number
}

/**
 * Ladeplatzhalter, der die spätere Struktur andeutet — verhindert, dass die
 * Seite beim Eintreffen der Daten springt.
 *
 * @example
 * ```tsx
 * {query.isPending ? <LoadingSkeleton variant="table" rows={8} columns={4} /> : <DataTable ... />}
 * ```
 */
export function LoadingSkeleton({
  variant = 'text',
  rows = 3,
  columns = 4,
  className,
  ...props
}: LoadingSkeletonProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn('w-full space-y-2', className)}
      {...props}
    >
      <span className="sr-only">Inhalte werden geladen</span>

      {variant === 'text' &&
        Array.from({ length: rows }, (_, index) => (
          <Skeleton key={index} className="h-4 w-full last:w-2/3" />
        ))}

      {variant === 'table' && (
        <div className="overflow-hidden rounded-lg border">
          <div className="flex gap-4 border-b bg-muted/50 px-4 py-3">
            {Array.from({ length: columns }, (_, index) => (
              <Skeleton key={index} className="h-4 flex-1" />
            ))}
          </div>
          {Array.from({ length: rows }, (_, rowIndex) => (
            <div key={rowIndex} className="flex gap-4 border-b px-4 py-3 last:border-b-0">
              {Array.from({ length: columns }, (_, columnIndex) => (
                <Skeleton key={columnIndex} className="h-4 flex-1" />
              ))}
            </div>
          ))}
        </div>
      )}

      {variant === 'card' && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: rows }, (_, index) => (
            <Skeleton key={index} className="h-32 w-full rounded-lg" />
          ))}
        </div>
      )}

      {variant === 'form' &&
        Array.from({ length: rows }, (_, index) => (
          <div key={index} className="space-y-2 pb-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-9 w-full" />
          </div>
        ))}
    </div>
  )
}
